import { memo, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, User, Eye, ChevronLeft, ChevronRight, LogOut, Store, Users, TrendingUp, GraduationCap, BookOpen, Shield, Menu, Search as SearchIcon, Package, ClipboardList, Flag, Building2, Bell, WifiOff, Inbox, UserRound, Network, Activity, Sparkles, Compass, ShieldCheck } from 'lucide-react';
import { useRealtimeStatus } from '@/hooks/useRealtimeStatus';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
import { useFohQueueCounts } from '@/hooks/tickets/useFohQueueCounts';
import { getFohNavStatus, type FohQueueCounts, type FohNavTone } from '@/lib/tickets/lifecycle/fohNavStatus';

const ZERO_FOH_COUNTS: FohQueueCounts = {
  pendingAdvisorAccept: 0,
  withAdvisor: 0,
  pendingTechAccept: 0,
};

const FOH_TONE_CLASSES: Record<FohNavTone, string> = {
  normal: '',
  returned: 'border-muted-foreground/50',
  aging: 'border-amber-500 text-amber-700 dark:text-amber-300',
  urgent: 'border-destructive text-destructive animate-pulse',
};

const FOH_TONE_BADGE_CLASSES: Record<FohNavTone, string> = {
  normal: 'bg-secondary text-secondary-foreground',
  returned: 'bg-secondary text-secondary-foreground',
  aging: 'bg-amber-500 text-white',
  urgent: 'bg-destructive text-destructive-foreground',
};
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { useNetworkSignals } from '@/hooks/useNetworkSignals';
import { SignalPanel } from '@/components/enterprise/SignalPanel';
import { ScopeSwitcher } from '@/components/floo-network/ScopeSwitcher';
import { OnboardingHUD } from '@/components/onboarding/OnboardingHUD';
import { useTutorial } from '@/contexts/TutorialContext';
import { cn } from '@/lib/utils';
import { format, addDays, subDays, isToday, getDay } from 'date-fns';
import { CalendarModal } from './CalendarModal';
import { supabase } from '@/integrations/supabase/client';
import { useDate } from '@/contexts/DateContext';
import { getShopHoursForDay, type ShopHours } from '@/utils/shopHoursUtils';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { useAuth } from '@/contexts/AuthContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useFormFactor } from '@/hooks/use-mobile';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { isCanonicalAppRole, EXPERIENCE_LABEL } from '@/lib/onboarding/onboardingExperience';
import { canRoleOpenUserGuide } from '@/lib/onboarding/routeReachability';
import { useUniversalSearch } from '@/hooks/useUniversalSearch';
import { UNIVERSAL_GROUP_ORDER, type UniversalResult } from '@/lib/search/universalSearch';
import { toast } from '@/hooks/use-toast';
import { actsAsLegacyRole, hasWorkRole, WORK_ROLES } from '@/lib/roles/multiRole';

// ARC-A5: wrap the component in `React.memo` so parent-tree re-renders
// that do NOT change TopBar's props (TopBar takes no props, so its
// effective props comparison is always equal) skip TopBar's render.
// Pre-A5 the bare `export const TopBar = () => {...}` would re-execute
// the entire body whenever any ancestor re-rendered — even though the
// only inputs that can actually drive TopBar's output are React-context
// values (useAuth / useDate / useTutorial / etc.), and those already
// notify their consumers directly via React's context propagation
// regardless of `memo`. The wrap is therefore a defense-in-depth
// optimization: it skips ancestor-driven re-executions, while context-
// driven re-renders continue to fire exactly as before. ARC-A2 already
// memoized the AuthContext provider value at the source, so consumer-
// reference churn is minimized for unchanged auth state; ARC-A5
// completes the picture by skipping unrelated parent-update churn.
//
// Behavior preservation: TopBar takes no props pre-A5 and post-A5;
// `memo`'s default shallow-equal-on-empty-props always returns true,
// so render-skip is unconditional WITH RESPECT TO PROPS. Context-
// driven re-renders are unaffected. Hooks rules of order are
// preserved (the wrapped function body is unchanged). DevTools
// displayName is set explicitly below.
export const TopBar = memo(() => {
  const navigate = useNavigate();
  // DCD-A2: dropped `setSelectedDate` destructure — go-to-today
  // actions now route through the canonical `setSelectedDayLabel` +
  // `getTodayInShopTz(shopTimezone)` pair (L641, L660). Browser-local
  // `new Date()` no longer drives shop-day selection from this file.
  const { selectedDate, setSelectedDayLabel } = useDate();
  const { shopTimezone } = useShopTimezone();
  // FIX-R39-HIGH-3: pull hasSchoolMembership so users with both shop +
  // school identities get a "School Portal" affordance in this header.
  // Without it, mixed-role users (e.g., a technician at Shop A who also
  // teaches at School B) had no in-shop way to discover/jump to /school.
  const { activeShop, userShops, setActiveShop, signOut, user, membership, hasSchoolMembership } = useAuth();
  const { canAccessSupportMode } = useSupportMode();
  const {
    startResolvedTutorial,
    onboardingOffer,
    currentStepData,
    nextStep,
    isActive,
    multiRoleExperiences,
    switchExperience,
  } = useTutorial();
  // Core-100 R4 CORE-BL (OD-14): the person's work roles, for route reachability
  // (the union ProtectedRoute admits) and the OTHER walkthroughs they are offered.
  const memberWorkRoles = useMemo(
    () => WORK_ROLES.filter((r) => hasWorkRole(membership as never, r)),
    [membership],
  );
  const otherWalkthroughs = useMemo(
    () => (multiRoleExperiences?.experiences ?? []).filter((e) => e !== onboardingOffer?.experience),
    [multiRoleExperiences, onboardingOffer],
  );


  const permissions = usePermissions();

  /**
   * CORE-BL: the walkthrough this person should be offered.
   *
   * Read from `TutorialContext`, not resolved here.
   *
   * TopBar used to build its own: `membership` straight from AuthContext,
   * its own `resolveOnboardingExperience`, its own `stepsForActiveExperience`
   * for the destination — while TutorialContext independently read the
   * canonical membership row and owned the resolved experience. Two
   * resolutions from two sources, agreeing only while nothing changes. During
   * a shop switch or a role change they disagree, and the menu would advertise
   * one walkthrough while `startResolvedTutorial` produced another — or
   * navigate to a destination belonging to a walkthrough that never started.
   *
   * Now the offer and the start come from the same resolution, so they cannot.
   * TopBar decides whether to render it and what to do on click; it does not
   * decide who the person is or what they are taught.
   */
  const onboardingEntry = useMemo(() => {
    if (!onboardingOffer) return null;
    return {
      label: `${onboardingOffer.label} walkthrough`,
      page: onboardingOffer.firstStepPage,
    };
  }, [onboardingOffer]);
  const connectEnabled = useFeatureFlag('optibayConnect');
  // Lifecycle v2 — slice 4A.1. Nav entry for /foh-queue is visible only
  // when the flag is on AND the user holds a FOH-capable role. Route
  // itself stays role-gated (ProtectedRoute) as the authority; this is
  // visibility-only.
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  const canViewFohQueue =
    custodyHandoffEnabled &&
    (permissions.isOwner ||
      permissions.isManager ||
      permissions.isServiceWriter ||
      permissions.isOrgAdmin);
  // Slice 4C.1: nav-button urgency indicator. Query is gated by
  // canViewFohQueue so the count only fires for FOH-capable roles when
  // the flag is on. See useFohQueueCounts + getFohNavStatus.
  const { data: fohCounts } = useFohQueueCounts(activeShop?.id ?? null, canViewFohQueue);
  const fohNavStatus = useMemo(
    () => getFohNavStatus(fohCounts ?? ZERO_FOH_COUNTS),
    [fohCounts],
  );
  // CDR-2.5: OCIS Inbox affordance — visible when ui_customerInbox is on
  // (Core-default-on per V-F-1g) and user has a non-tech role. Route at
  // /inbox is gated to OWNER/MANAGER/SERVICE_WRITER; mirror that here.
  const customerInboxEnabled = useExposureFlag('ui_customerInbox');
  // Multi-role: the /inbox route admits EFFECTIVE roles (ProtectedRoute → actsAsLegacyRole); the
  // link must ask the same question, or a technician who also advises can use the inbox but never
  // sees the way in.
  const canViewInbox =
    customerInboxEnabled &&
    actsAsLegacyRole(membership as never, 'OWNER', 'MANAGER', 'SERVICE_WRITER');
  const formFactor = useFormFactor();
  const { data: isInternalAdmin } = useIsInternalAdmin();
  // Platform Admin group gating. `data` is `boolean | undefined`: undefined
  // while the is_internal_admin RPC is in flight, and the hook's queryFn
  // already coerces an RPC error to `false`. Comparing against `true`
  // therefore fails closed on BOTH loading and error — an ordinary user
  // never sees the group flash before authorization resolves.
  const showPlatformAdmin = isInternalAdmin === true;
  // Active-route styling for the Quality Command Center entry. Prefix (not
  // exact) matching so the item stays lit on descendant routes such as
  // /admin/quality/runs/:runId. Guarded on a path boundary so a sibling
  // route like /admin/quality-foo could never light it up.
  const { pathname } = useLocation();
  const isQualityRouteActive =
    pathname === '/admin/quality' || pathname.startsWith('/admin/quality/');
  const { showShopSwitcher, canViewOrgDashboard, canViewInScopeTalent } = useEnterpriseScope();
  const isPhone = formFactor === 'phone';
  /**
   * The full navigation row measures ~875px. A tablet is 768–1200px wide, so
   * "not a phone" was never the right test for showing it: at 768x1024 the row
   * pushed the page 742px sideways, and at 820x1180 689px. Measured on the
   * real stack.
   *
   * Tablets therefore get the same Sheet the phone gets. That is not a
   * reduction in capability — the Sheet carries the identical route list, so
   * every destination stays reachable; it is a change of presentation for a
   * width the row genuinely does not fit. Desktop is unaffected and keeps the
   * full bar.
   */
  const useCollapsedNav = formFactor === 'phone' || formFactor === 'tablet';
  const [showCalendar, setShowCalendar] = useState(false);
  // R37: Surface realtime connection state so operators see when live
  // updates have stalled (page may be showing stale data until reconnect).
  const realtimeStatus = useRealtimeStatus();
  const [shopHours, setShopHours] = useState<Record<string, { open: string; close: string }>>({});
  const [holidays, setHolidays] = useState<string[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  // Core-100 CORE-AY: the query typed into the global search; results come from the server.
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [showSignalPanel, setShowSignalPanel] = useState(false);
  const { signals, activeCount, isLoading: signalsLoading, acknowledgeSignal, dismissSignal } = useNetworkSignals();
  const forceAccountMenuOpen = isActive && ['user-guide-select', 'user-guide-intro-user', 'account-settings-intro'].includes(currentStepData?.id ?? '');

  useEffect(() => {
    if (forceAccountMenuOpen) {
      setAccountMenuOpen(true);
    }
  }, [forceAccountMenuOpen]);

  useEffect(() => {
    loadShopSchedule();

    // Keyboard shortcut for search
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // ARC-A3 / ARC-A5 history: the search used to prefetch tickets when opened (gated on showSearch,
  // keyed on activeShop?.id). That prefetch is RETIRED by Core-100 CORE-AY below.
  // Core-100 CORE-AY: universal search. The old global search prefetched at most 1,000 tickets and
  // filtered them in the browser by customer name and vehicle — no ticket number, no customers by phone /
  // email / VIN / plate, no employees, and a corpus silently bounded by the prefetch. It now asks the
  // server: tickets of every date and employees (universal_search, under the caller's RLS) and customers /
  // vehicles through customer-search (blind index, masked).
  const universal = useUniversalSearch(searchQuery, {
    enabled: showSearch,
    // R4: an employee result opens the person — their Passport, or (for roster editors) their roster record.
    canOpenRoster: permissions.isManager || permissions.isOwner || permissions.isServiceWriter,
  });

  const loadShopSchedule = async () => {
    if (!activeShop) return;
    
    const { data } = await supabase
      .from('shop_profile')
      .select('hours, holidays')
      .eq('shop_id', activeShop.id)
      .limit(1)
      .maybeSingle();

    if (data) {
      setShopHours((data.hours as any) || {});
      setHolidays(((data.holidays as any[]) || []).map(h => h.date));
    }
  };

  /**
   * PTP-024 — resolve the day through the canonical helper, not an inline map.
   *
   * This used to index `shopHours['Monday']` with a capitalized name. But
   * `shop_profile.hours` is canonically stored LOWERCASE — that is what
   * onboarding's ShopBasicsStep writes and what DEFAULT_SHOP_HOURS_MON_FRI_8_5
   * declares — so the lookup missed on every real shop, every day read as
   * CLOSED, and `findNextOpenDay` below walked its whole 14-day budget and
   * handed back the date it started from. The day chevrons did nothing at all.
   *
   * `getShopHoursForDay` already accepts either casing and resolves the weekday
   * in SHOP time rather than browser time, which is the correct question to ask
   * about a shop's day. No new normalization is introduced here on purpose.
   */
  const isShopOpen = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    if (holidays.includes(dateStr)) return false;

    const hours = getShopHoursForDay(date, shopHours as ShopHours, shopTimezone);
    return !!(hours?.open && hours?.close);
  };

  const findNextOpenDay = (date: Date, direction: 'next' | 'prev'): Date => {
    // During the User Tutorial step 2 (date-navigation), always move by one day
    if (isActive && currentStepData?.id === 'date-navigation') {
      return direction === 'next' ? addDays(date, 1) : subDays(date, 1);
    }

    let checkDate = direction === 'next' ? addDays(date, 1) : subDays(date, 1);
    let attempts = 0;
    
    while (!isShopOpen(checkDate) && attempts < 14) {
      checkDate = direction === 'next' ? addDays(checkDate, 1) : subDays(checkDate, 1);
      attempts++;
    }
    
    return isShopOpen(checkDate) ? checkDate : date;
  };

  const handlePreviousDay = () => {
    const newDate = findNextOpenDay(selectedDate, 'prev');
    setSelectedDayLabel(format(newDate, 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const newDate = findNextOpenDay(selectedDate, 'next');
    setSelectedDayLabel(format(newDate, 'yyyy-MM-dd'));
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      Active: 'bg-green-500',
      Paused: 'bg-yellow-500',
      Completed: 'bg-blue-500',
      Deleted: 'bg-red-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  const handleUniversalSelect = (result: UniversalResult) => {
    // R4: a result with nowhere this viewer can go stays in the list with its note; it never closes the search
    // into nothing.
    if (!result.target.path) return;
    const path = result.target.path;
    setShowSearch(false);
    setSearchQuery('');
    if (result.target.dayLabel) setSelectedDayLabel(result.target.dayLabel);
    // Let the day change land before the board highlights the ticket.
    setTimeout(() => navigate(path), 0);
  };


  return (
    <>
      <header data-testid="top-bar" className="sticky top-0 z-50 w-full border-b bg-card shadow-sm">
        {/* PTP-012 — the roomier spacing used to start at `md`, which IS 768px,
            so a tablet got desktop padding and desktop gaps in a bar that does
            not fit them: measured 46px of PAGE overflow at 768 with the right
            cluster ending at x=814. Moving the step-up to `lg` keeps the
            comfortable desktop bar and gives the tablet the compact one it
            needs. `min-w-0` lets the left section actually shrink instead of
            forcing the overflow onto its siblings. */}
        <div className="flex h-16 items-center justify-between px-4 lg:px-6 gap-2 lg:gap-6 min-w-0">
          {/* Left: Logo & Navigation */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <h1 
              className="text-base md:text-lg font-bold text-foreground cursor-pointer hover:opacity-80 transition-opacity whitespace-nowrap"
              onClick={() => {
                navigate('/');
                if (isActive && currentStepData?.id === 'analytics-technicians') {
                  nextStep();
                }
              }}
            >
              {isPhone ? 'OptiBay' : (activeShop?.name || 'OptiBay')}
            </h1>

            {useCollapsedNav ? (
              // Phone AND tablet: hamburger + sheet (same routes, see above)
              <Sheet open={showMobileMenu} onOpenChange={setShowMobileMenu}>
                <SheetTrigger asChild>
                  {/* PTP-017 — the ONLY way a phone reaches Analytics, Customers,
                      Birdseye and the rest. It had no test id, so route
                      discoverability could not be measured without guessing at
                      an icon class. Identifier only: no behaviour changes. */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    data-testid="mobile-menu-toggle"
                    aria-label="Open navigation menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80" data-testid="mobile-nav-sheet">
                  <SheetHeader>
                    <SheetTitle>{activeShop?.name || 'OptiBay'}</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col gap-2 mt-6">
                    {permissions.canCreateTickets && (
                      <Button
                        data-testid="create-ticket-btn"
                        variant="default"
                        size="sm"
                        onClick={() => {
                          navigate('/create-ticket');
                          setShowMobileMenu(false);
                        }}
                        className="justify-start gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Create Ticket
                      </Button>
                    )}
                    {/* Top-bar Team button removed — Team management is
                        now reached from Shop Profile → Teams tab. The
                        route `/team-management` remains live for direct
                        URL / bookmark access. */}
                    {permissions.canViewShopProfile && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/shop-profile');
                          setShowMobileMenu(false);
                          if (isActive && currentStepData?.id === 'navigate-to-profile') {
                            nextStep();
                          }
                        }}
                        className="justify-start gap-2"
                        data-testid="nav-profile-button"
                      >
                        <User className="h-4 w-4" />
                        Profile
                      </Button>
                    )}
                    {permissions.canViewBirdseye && (
                      <Button
                        data-testid="birdseye-link"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/birdseye');
                          setShowMobileMenu(false);
                        }}
                        className="justify-start gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Birdseye
                      </Button>
                    )}
                    {(permissions.isManager || permissions.isOwner || permissions.isServiceWriter) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/analytics');
                          setShowMobileMenu(false);
                        }}
                        className="justify-start gap-2"
                        data-testid="nav-analytics-button"
                      >
                        <TrendingUp className="h-4 w-4" />
                        Analytics
                      </Button>
                    )}
                    {/* /customers is ProtectedRoute(OWNER, MANAGER,
                        SERVICE_WRITER, TECHNICIAN + work role
                        DETAIL_SPECIALIST) — OD-34: technicians and detail
                        specialists read the shop's customers. This predicate
                        is that boundary and nothing wider. */}
                    {(permissions.isManager || permissions.isOwner || permissions.isServiceWriter || permissions.isTechnician || permissions.isDetailSpecialist) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/customers');
                          setShowMobileMenu(false);
                        }}
                        className="justify-start gap-2"
                        data-testid="nav-customers-button-mobile"
                      >
                        <Users className="h-4 w-4" />
                        Customers &amp; Vehicles
                      </Button>
                    )}
                    {/* Floo Network — Phase 2 navigation grouping.
                        Each item is route-gated by ProtectedRoute(OWNER/ORG_ADMIN/MANAGER)
                        and additionally scope-gated inside the page.
                        Visibility predicates come from useEnterpriseScope (canViewOrgDashboard,
                        canViewInScopeTalent), which already encode the shop-only exclusion —
                        single-shop / Core beta users do not satisfy these and never see this
                        group. Region/district language is deliberately avoided in labels. */}
                    {/* Stage 2 — mobile OptiNetwork group is hidden for
                        single-shop orgs (account-menu OptiNetwork item remains
                        the entry point for those users). Multi-shop orgs keep
                        the full mobile group. */}
                    {(canViewOrgDashboard || canViewInScopeTalent) && userShops.length > 1 && (
                      <>
                        <div className="border-t my-2" />
                        <p className="text-xs font-semibold text-muted-foreground px-2 mb-1">OptiNetwork</p>
                        {/* Phase 6 — mobile scope switcher. Hidden when
                            the user has no choice to make. */}
                        <ScopeSwitcher
                          variant="mobile"
                          onPick={() => setShowMobileMenu(false)}
                          className="mb-2"
                        />
                        {canViewOrgDashboard && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/admin/optinetwork/map');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-map-button"
                          >
                            <Network className="h-4 w-4" />
                            OptiNetwork Map
                          </Button>
                        )}
                        {canViewOrgDashboard && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/org');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-org-button"
                          >
                            <Building2 className="h-4 w-4" />
                            Company Dashboard
                          </Button>
                        )}
                        {canViewOrgDashboard && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/org/intelligence');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-intelligence-button"
                          >
                            <Activity className="h-4 w-4" />
                            Cross-Shop Intelligence
                          </Button>
                        )}
                        {canViewOrgDashboard && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/org/recommendations');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-recommendations-button"
                          >
                            <Sparkles className="h-4 w-4" />
                            Recommendations
                          </Button>
                        )}
                        {canViewInScopeTalent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/enterprise/talent');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-talent-button"
                          >
                            <Users className="h-4 w-4" />
                            Talent Directory
                          </Button>
                        )}
                        {canViewInScopeTalent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/enterprise/talent/analytics');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-talent-analytics-button"
                          >
                            <TrendingUp className="h-4 w-4" />
                            Talent Analytics
                          </Button>
                        )}
                        {canViewInScopeTalent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/enterprise/training');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-training-button"
                          >
                            <GraduationCap className="h-4 w-4" />
                            Training Visibility
                          </Button>
                        )}
                        {canViewInScopeTalent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/enterprise/talent/suggestions');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-suggestions-button"
                          >
                            <Compass className="h-4 w-4" />
                            Talent Fit Suggestions
                          </Button>
                        )}
                        {canViewOrgDashboard && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigate('/enterprise/intelligence/governance');
                              setShowMobileMenu(false);
                            }}
                            className="justify-start gap-2"
                            data-testid="nav-floo-governance-button"
                          >
                            <Shield className="h-4 w-4" />
                            Intelligence Governance
                          </Button>
                        )}
                      </>
                    )}
                    {/* CDR-2.5: OCIS Inbox affordance (mobile menu) */}
                    {canViewInbox && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/inbox');
                          setShowMobileMenu(false);
                        }}
                        className="justify-start gap-2"
                        data-testid="nav-inbox-button"
                      >
                        <Inbox className="h-4 w-4" />
                        Inbox
                      </Button>
                    )}
                    {/* Lifecycle v2 — slice 4A.1 + 4C.1. FOH queue mobile nav */}
                    {/* with tone-aware urgency indicator. */}
                    {canViewFohQueue && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigate('/foh-queue');
                          setShowMobileMenu(false);
                        }}
                        className={cn(
                          'justify-start gap-2',
                          FOH_TONE_CLASSES[fohNavStatus.tone],
                        )}
                        data-testid="nav-foh-queue-button"
                        data-foh-tone={fohNavStatus.tone}
                        aria-label={
                          fohNavStatus.total > 0
                            ? `FOH Workboard — ${fohNavStatus.total} need attention (${fohNavStatus.tone})`
                            : 'FOH Workboard'
                        }
                      >
                        <UserRound className="h-4 w-4" />
                        FOH Workboard
                        {fohNavStatus.total > 0 && (
                          <span
                            className={cn(
                              'ml-1 inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5 text-[10px] font-semibold',
                              FOH_TONE_BADGE_CLASSES[fohNavStatus.tone],
                            )}
                          >
                            {fohNavStatus.total}
                          </span>
                        )}
                      </Button>
                    )}
                    {/* Top-bar Connect and Calendar buttons removed —
                        Connect is reached from Shop Profile → Connect tab
                        (or via direct `/connect`), and the calendar modal
                        is now opened by clicking the inline date in the
                        top-bar (see `calendar-date-button`). */}

                    {/* Shop selector in mobile menu.
                        Phase 2: Organization / Talent links moved out of this block
                        into the Floo Network grouping above, so this block is now a
                        pure shop switcher. Reachability for the same eligible users
                        is preserved (and broadened — single-shop OWNERs without the
                        scope-switcher now get Floo Network access too). */}
                    {(userShops.length > 1 || showShopSwitcher) && (
                      <>
                        <div className="border-t my-2" />
                        <p className="text-xs font-semibold text-muted-foreground px-2 mb-1">Switch Shop</p>
                        {userShops.map((shop) => (
                          <Button
                            key={shop.id}
                            variant={activeShop?.id === shop.id ? "secondary" : "ghost"}
                            size="sm"
                            onClick={() => {
                              setActiveShop(shop.id);
                              setShowMobileMenu(false);
                            }}
                            className="justify-start"
                          >
                            <Store className="h-4 w-4 mr-2" />
                            {shop.name}
                          </Button>
                        ))}
                      </>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            ) : (
              // Desktop: Full navigation bar
              <nav className="flex gap-2">
                {permissions.canCreateTickets && (
                  <Button
                    data-testid="create-ticket-btn"
                    variant="default"
                    size="sm"
                    onClick={() => navigate('/create-ticket')}
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Create Ticket
                  </Button>
                )}
                {/* Top-bar Team button removed — Team management is now
                    reached from Shop Profile → Teams tab. The route
                    `/team-management` is preserved for direct access. */}
                {permissions.canViewShopProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigate('/shop-profile');
                      if (isActive && currentStepData?.id === 'navigate-to-profile') {
                        nextStep();
                      }
                    }}
                    className="gap-2"
                    data-testid="nav-profile-button"
                  >
                    <User className="h-4 w-4" />
                    Profile
                  </Button>
                )}
                {permissions.canViewBirdseye && (
                  <Button
                    data-testid="birdseye-link"
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/birdseye')}
                    className="gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    Birdseye
                  </Button>
                )}
                {(permissions.isManager || permissions.isOwner || permissions.isServiceWriter) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/analytics')}
                    className="gap-2"
                    data-testid="nav-analytics-button"
                  >
                    <TrendingUp className="h-4 w-4" />
                    Analytics
                  </Button>
                )}
                {/* Same boundary as the route and the mobile entry above (OD-34). */}
                {(permissions.isManager || permissions.isOwner || permissions.isServiceWriter || permissions.isTechnician || permissions.isDetailSpecialist) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/customers')}
                    className="gap-2"
                    data-testid="nav-customers-button"
                  >
                    <Users className="h-4 w-4" />
                    Customers &amp; Vehicles
                  </Button>
                )}
                {/* Floo Network — Phase 2 nav grouping.
                    Visibility predicates from useEnterpriseScope encode the
                    shop-only exclusion (canViewOrgDashboard/canViewInScopeTalent
                    are false for technicians, advisors, and shop-only managers
                    who lack scope access — Core beta / single-shop users do not
                    see this dropdown). Each item is also route-gated by
                    ProtectedRoute(OWNER/ORG_ADMIN/MANAGER) and scope-gated
                    inside the page. Labels avoid "region" / "district" wording
                    per the Phase 1 plan. */}
                {/* Phase 6 — scope switcher renders only when the user has
                    more than one allowed scope option. The component itself
                    handles the null/single-option case so a single-shop user
                    sees nothing here. */}
                {(canViewOrgDashboard || canViewInScopeTalent) && userShops.length > 1 && (
                  <ScopeSwitcher variant="desktop" />
                )}
                {/* Stage 2 — prominent top-bar OptiNetwork dropdown is shown
                    only when the org has more than one accessible shop. Single
                    -shop / Core beta users keep the cleaner top bar and reach
                    OptiNetwork from the account dropdown instead. The full
                    visibility predicate (canViewOrgDashboard ||
                    canViewInScopeTalent) is still required so a single-shop
                    user without OptiNetwork permissions never sees it. */}
                {(canViewOrgDashboard || canViewInScopeTalent) && userShops.length > 1 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-2"
                        data-testid="nav-floo-network-button"
                      >
                        <Network className="h-4 w-4" />
                        OptiNetwork
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel>OptiNetwork</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {canViewOrgDashboard && (
                        <DropdownMenuItem
                          onClick={() => navigate('/admin/optinetwork/map')}
                          data-testid="nav-floo-map-item"
                        >
                          <Network className="h-4 w-4 mr-2" />
                          OptiNetwork Map
                        </DropdownMenuItem>
                      )}
                      {canViewOrgDashboard && (
                        <DropdownMenuItem
                          onClick={() => navigate('/org')}
                          data-testid="nav-floo-org-item"
                        >
                          <Building2 className="h-4 w-4 mr-2" />
                          Company Dashboard
                        </DropdownMenuItem>
                      )}
                      {canViewOrgDashboard && (
                        <DropdownMenuItem
                          onClick={() => navigate('/org/intelligence')}
                          data-testid="nav-floo-intelligence-item"
                        >
                          <Activity className="h-4 w-4 mr-2" />
                          Cross-Shop Intelligence
                        </DropdownMenuItem>
                      )}
                      {canViewOrgDashboard && (
                        <DropdownMenuItem
                          onClick={() => navigate('/org/recommendations')}
                          data-testid="nav-floo-recommendations-item"
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Recommendations
                        </DropdownMenuItem>
                      )}
                      {canViewInScopeTalent && canViewOrgDashboard && (
                        <DropdownMenuSeparator />
                      )}
                      {canViewInScopeTalent && (
                        <DropdownMenuItem
                          onClick={() => navigate('/enterprise/talent')}
                          data-testid="nav-floo-talent-item"
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Talent Directory
                        </DropdownMenuItem>
                      )}
                      {canViewInScopeTalent && (
                        <DropdownMenuItem
                          onClick={() => navigate('/enterprise/talent/analytics')}
                          data-testid="nav-floo-talent-analytics-item"
                        >
                          <TrendingUp className="h-4 w-4 mr-2" />
                          Talent Analytics
                        </DropdownMenuItem>
                      )}
                      {canViewInScopeTalent && (
                        <DropdownMenuItem
                          onClick={() => navigate('/enterprise/training')}
                          data-testid="nav-floo-training-item"
                        >
                          <GraduationCap className="h-4 w-4 mr-2" />
                          Training Visibility
                        </DropdownMenuItem>
                      )}
                      {canViewInScopeTalent && (
                        <DropdownMenuItem
                          onClick={() => navigate('/enterprise/talent/suggestions')}
                          data-testid="nav-floo-suggestions-item"
                        >
                          <Compass className="h-4 w-4 mr-2" />
                          Talent Fit Suggestions
                        </DropdownMenuItem>
                      )}
                      {canViewOrgDashboard && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => navigate('/enterprise/intelligence/governance')}
                            data-testid="nav-floo-governance-item"
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            Intelligence Governance
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {/* CDR-2.5: OCIS Inbox affordance (desktop nav) */}
                {canViewInbox && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/inbox')}
                    className="gap-2"
                    data-testid="nav-inbox-button"
                  >
                    <Inbox className="h-4 w-4" />
                    Inbox
                  </Button>
                )}
                {/* Lifecycle v2 — slice 4A.1 + 4C.1. FOH queue desktop nav */}
                {/* with tone-aware urgency indicator. Tone derives from */}
                {/* getFohNavStatus(counts) — see fohNavStatus.ts. */}
                {canViewFohQueue && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/foh-queue')}
                    className={cn('gap-2', FOH_TONE_CLASSES[fohNavStatus.tone])}
                    data-testid="nav-foh-queue-button"
                    data-foh-tone={fohNavStatus.tone}
                    aria-label={
                      fohNavStatus.total > 0
                        ? `FOH Workboard — ${fohNavStatus.total} need attention (${fohNavStatus.tone})`
                        : 'FOH Workboard'
                    }
                  >
                    <UserRound className="h-4 w-4" />
                    FOH Workboard
                    {fohNavStatus.total > 0 && (
                      <span
                        className={cn(
                          'ml-0.5 inline-flex items-center justify-center rounded-full px-1.5 min-w-[1.25rem] h-5 text-[10px] font-semibold',
                          FOH_TONE_BADGE_CLASSES[fohNavStatus.tone],
                        )}
                        data-testid="nav-foh-queue-count"
                      >
                        {fohNavStatus.total}
                      </span>
                    )}
                  </Button>
                )}
                {/* Top-bar Connect button removed — Connect is now
                    reached from Shop Profile → Connect tab (or via
                    direct `/connect`). */}
                {/* Top-bar Calendar button removed — clicking the inline
                    date in the top-bar (see `calendar-date-button`)
                    opens the same CalendarModal. */}
              </nav>
            )}
          </div>

          {/* Center: Search (hidden on mobile) */}
          {!isPhone && (
            <div className="flex-1 max-w-md" data-onboarding-id="search-bar">
              <Input
                type="search"
                placeholder="Search tickets, customers, vehicles, employees (Ctrl/Cmd+K)..."
                className="w-full bg-background/50 cursor-pointer"
                onClick={() => setShowSearch(true)}
                readOnly
              />
            </div>
          )}

        {/* Right: Date Navigation & User Menu */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Search icon on mobile */}
          {isPhone && (
            <Button 
              variant="ghost" 
              size="icon"
              className="h-9 w-9"
              data-onboarding-id="search-bar"
              onClick={() => setShowSearch(true)}
            >
              <SearchIcon className="h-4 w-4" />
            </Button>
          )}

          {/* R37: Realtime offline indicator. Only shown when explicitly
              disconnected so happy-path UI is unchanged. Click is a no-op;
              the indicator is informational because the Supabase JS client
              auto-retries the WebSocket and most blips self-heal. */}
          {realtimeStatus === 'disconnected' && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    role="status"
                    aria-label="Realtime updates offline"
                    className="inline-flex items-center justify-center h-9 w-9 rounded-md text-destructive"
                    data-testid="realtime-offline-indicator"
                  >
                    <WifiOff className="h-4 w-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Live updates are offline. The page may be showing stale data — refresh if needed.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Signal Bell (enterprise users only) */}
          {canViewOrgDashboard && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 relative"
              onClick={() => setShowSignalPanel(true)}
            >
              <Bell className="h-4 w-4" />
              {activeCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold min-w-[16px] h-4 rounded-full flex items-center justify-center px-1">
                  {activeCount}
                </span>
              )}
            </Button>
          )}

          {/* Shop Selector (desktop only, mobile has it in hamburger).
              Phase 2: Organization / Talent entries moved out of this dropdown
              into the dedicated Floo Network grouping above, so this dropdown is
              now a pure shop switcher. Reachability for the same eligible users
              is preserved by the Floo Network grouping's own predicates. */}
          {/* PTP-017 — DESKTOP ONLY, and this was a P0 on a tablet.

              The gate used to be `!isPhone`, so a tablet rendered this shop
              button IN ADDITION to the "Switch Shop" section the navigation
              Sheet already carries. At 768px that extra control overflowed the
              bar until the account menu sat on top of the hamburger: measured
              on tablet Chromium AND tablet WebKit, Playwright refused the tap
              with "account-menu-button ... intercepts pointer events".

              The hamburger is the ONLY navigation on a tablet, so a user who
              belongs to more than one shop could not open navigation AT ALL —
              every route became undiscoverable for exactly the people most
              likely to need to move between shops. Reproduced against baseline
              CSS, so it predates the touch-target work.

              Collapsed form factors switch shops in the Sheet; desktop, which
              has the room and no Sheet, keeps this. */}
          {formFactor === 'desktop' && (userShops.length > 1 || showShopSwitcher) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Store className="h-4 w-4" />
                  {activeShop?.name || 'Select Shop'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Switch Shop</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {userShops.map((shop) => (
                  <DropdownMenuItem
                    key={shop.id}
                    onClick={() => setActiveShop(shop.id)}
                    className={cn(activeShop?.id === shop.id && "bg-accent")}
                  >
                    {shop.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <div className="flex items-center gap-1" data-testid="inline-calendar">
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={handlePreviousDay}
              aria-label="Previous day"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {!isPhone ? (
              <>
                {/* Clicking the inline date now opens the full
                    CalendarModal — replaces the removed standalone
                    "Calendar" top-bar button. The chevron arrows + the
                    explicit "Today" button still do day-by-day nav. */}
                <Button
                  variant={isToday(selectedDate) ? "default" : "outline"}
                  size="sm"
                  className={cn(
                    "whitespace-nowrap",
                    isToday(selectedDate) && "bg-green-700 hover:bg-green-800"
                  )}
                  onClick={() => setShowCalendar(true)}
                  data-testid="calendar-date-button"
                  aria-label="Open calendar"
                >
                  {/* PTP-012 — the TABLET cannot afford the long date.
                      At 768px this label renders "EEE, MMM d, yyyy" at
                      `text-lg font-bold whitespace-nowrap` and measures 285px;
                      together with the chevrons and the rest of the bar it put
                      the whole PAGE 119px past the viewport, measured on both
                      engines with a card on the board. Desktop has the room and
                      keeps the full date; tablet gets the compact form the phone
                      already uses, which is ~90px and clears the overflow with
                      margin to spare. */}
                  {formFactor === 'desktop'
                    ? format(selectedDate, 'EEE, MMM d, yyyy')
                    : isToday(selectedDate)
                      ? 'Today'
                      : format(selectedDate, 'MMM d')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedDayLabel(getTodayInShopTz(shopTimezone));
                    // Auto-advance tutorial when selecting today on calendar step
                    if (isActive && currentStepData?.id === 'calendar-modal') {
                      nextStep();
                    }
                  }}
                >
                  Today
                </Button>
              </>
            ) : (
              <Button
                variant={isToday(selectedDate) ? "default" : "outline"}
                size="sm"
                className={cn(
                  "text-xs px-2",
                  isToday(selectedDate) && "bg-green-700 hover:bg-green-800"
                )}
                onClick={() => {
                  // Mobile inline date: open the full CalendarModal.
                  // Replaces the removed standalone "Calendar" top-bar
                  // button. Selecting today is now done from inside the
                  // modal; the chevron arrows still do day-by-day nav.
                  setShowCalendar(true);
                  if (isActive && currentStepData?.id === 'calendar-modal') {
                    nextStep();
                  }
                }}
                data-testid="calendar-date-button"
                aria-label="Open calendar"
              >
                {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d')}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="icon" 
              className="h-8 w-8 md:h-9 md:w-9"
              onClick={handleNextDay}
              aria-label="Next day"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* User Menu or Sign In */}
          {user ? (
            <DropdownMenu
              open={forceAccountMenuOpen || accountMenuOpen}
              onOpenChange={(open) => {
                if (forceAccountMenuOpen && !open) return;
                setAccountMenuOpen(open);
                if (open && isActive && currentStepData?.id === 'user-guide-intro') {
                  nextStep();
                }
              }}
            >
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" data-testid="account-menu-button" aria-label="Account menu">
                  <User className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.email}</span>
                    <span className="text-xs text-muted-foreground font-normal">
                      {membership?.role}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="account-settings-link" onClick={() => navigate('/account-settings')}>
                  <User className="h-4 w-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
                {/* FIX-R39-HIGH-3: cross-context jump for mixed-role users.
                    Only renders when the user has a school membership in
                    addition to their shop role; school-only users land
                    directly on /school via routing. */}
                {hasSchoolMembership && (
                  <DropdownMenuItem onClick={() => navigate('/school')}>
                    <GraduationCap className="h-4 w-4 mr-2" />
                    School Portal
                  </DropdownMenuItem>
                )}
                {/*
                  CORE-BL: one entry point, offering the walkthrough that
                  belongs to THIS person.

                  There used to be two hard-coded items: a manager tour for
                  MANAGER and OWNER, and a general tour for everyone whose
                  role was not literally TECHNICIAN. So a technician — among
                  the heaviest users of the product — had no way to reach
                  onboarding from the menu at all, which meant that even once
                  content existed for them, nothing would have surfaced it.

                  The entry now asks the same resolver the automatic router
                  uses, so the menu and the routing cannot disagree about
                  what somebody should be taught.
                */}
                {onboardingEntry && (
                  <DropdownMenuItem
                    data-testid="onboarding-entry-point"
                    onClick={async () => {
                      // Navigate ONLY if the tutorial actually started.
                      //
                      // `startTutorial` used to return `Promise<void>`, so a
                      // refusal — most importantly a failed read of existing
                      // progress — was indistinguishable from a start. The
                      // person was navigated away to a page with no tour on
                      // it and told nothing, which reads as the feature being
                      // broken rather than as the app declining to overwrite
                      // their place.
                      // No flow is passed. The context starts THIS person's
                      // resolved experience; a caller-supplied durable key is
                      // how a technician could have been persisted under the
                      // `manager` contract.
                      const result = await startResolvedTutorial();
                      if (result.status === 'started') {
                        navigate(onboardingEntry.page);
                        return;
                      }
                      toast({
                        title: 'Walkthrough not started',
                        description:
                          result.reason === 'progress_failed'
                            ? 'We could not read where you had got to, and we will not restart your walkthrough over progress we cannot see. Please try again shortly.'
                            : result.reason === 'save_failed'
                              ? 'The walkthrough is open, but we could not save your place. Your progress may not survive a refresh.'
                              : 'We could not start the walkthrough just yet. Please try again shortly.',
                        variant: 'destructive',
                      });
                    }}
                  >
                    <GraduationCap className="h-4 w-4 mr-2" />
                    {onboardingEntry.label}
                  </DropdownMenuItem>
                )}
                {/* CORE-BL: shown only to roles the router will actually let
                    in. `/user-guide` is guarded to OWNER, MANAGER,
                    SERVICE_WRITER, TECHNICIAN and ORG_ADMIN, and this item
                    rendered for everybody — so a dispatcher, parts person,
                    auditor, region or district manager was offered a link
                    whose only possible outcome was a 403 page.

                    This changes PRESENTATION only. Route authority lives in
                    `App.tsx` and is not touched: the guard is read, never
                    widened. */}
                {/* Core-100 R4 CORE-BL (OD-14): a person who does two jobs is
                    offered both walkthroughs; switching makes the other one
                    the entry above (its own progress row). */}
                {otherWalkthroughs.map((experience) => (
                  <DropdownMenuItem
                    key={experience}
                    data-testid={`onboarding-switch-${experience}`}
                    onClick={() => {
                      if (switchExperience(experience)) {
                        toast({
                          title: `${EXPERIENCE_LABEL[experience]} walkthrough selected`,
                          description: 'Open it from this menu when you are ready.',
                        });
                      }
                    }}
                  >
                    <GraduationCap className="h-4 w-4 mr-2" />
                    {`Switch to: ${EXPERIENCE_LABEL[experience]} walkthrough`}
                  </DropdownMenuItem>
                ))}
                {canRoleOpenUserGuide(
                  isCanonicalAppRole(membership?.role) ? membership.role : null,
                  memberWorkRoles,
                ) && (
                  <DropdownMenuItem data-testid="user-guide-link" onClick={() => navigate('/user-guide')}>
                    <BookOpen className="h-4 w-4 mr-2" />
                    User Guide
                  </DropdownMenuItem>
                )}
                {/* Stage 2 — OptiNetwork entry in the account dropdown.
                    Visible to anyone who satisfies the same enterprise
                    visibility predicate the top-bar OptiNetwork dropdown
                    uses (so a single-shop user with the right role can
                    still reach OptiNetwork even when the prominent
                    top-bar dropdown is hidden). */}
                {(canViewOrgDashboard || canViewInScopeTalent) && (
                  <DropdownMenuItem
                    data-testid="account-menu-optinetwork"
                    onClick={() => navigate('/admin/optinetwork')}
                  >
                    <Network className="h-4 w-4 mr-2" />
                    OptiNetwork
                  </DropdownMenuItem>
                )}
                {canAccessSupportMode && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/support/shops')}>
                      <Shield className="h-4 w-4 mr-2" />
                      Support Mode
                    </DropdownMenuItem>
                  </>
                )}
                {showPlatformAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    {/* Platform-engineering tools — internal OptiBay admins
                        only, deliberately kept out of the shop-workflow nav.
                        This block sits outside the `isPhone` fork, so the one
                        declaration serves both the desktop dropdown and the
                        phone account menu (no duplicate entry to drift). */}
                    <DropdownMenuLabel className="text-xs text-muted-foreground font-semibold">
                      Platform Admin
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      data-testid="platform-admin-quality-link"
                      aria-current={isQualityRouteActive ? 'page' : undefined}
                      className={cn(isQualityRouteActive && 'bg-accent text-accent-foreground')}
                      onClick={() => navigate('/admin/quality')}
                    >
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Quality Command Center
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/feature-flags')}>
                      <Flag className="h-4 w-4 mr-2" />
                      Feature Flags
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/beta-leads')}>
                      <Users className="h-4 w-4 mr-2" />
                      Beta Leads
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/creator')}>
                      <ClipboardList className="h-4 w-4 mr-2" />
                      Creator Portal
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={signOut} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="default" size="sm" onClick={() => navigate('/auth')}>
              Sign In
            </Button>
          )}
        </div>
      </div>
      </header>

      {/* Onboarding HUD in Sidebar */}
      <div className="fixed left-6 bottom-6 z-40 w-80">
        <OnboardingHUD />
      </div>

      {/* Calendar Modal */}
      <CalendarModal 
        open={showCalendar}
        onOpenChange={setShowCalendar}
        currentDate={selectedDate}
        onDateSelect={setSelectedDayLabel}
      />

      {/* Search Dialog */}
      <CommandDialog open={showSearch} onOpenChange={(o) => { setShowSearch(o); if (!o) setSearchQuery(''); }} shouldFilter={false}>
        <CommandInput
          placeholder="Ticket #, customer, phone, email, VIN, plate, vehicle or employee..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList data-testid="universal-search-results">
          {searchQuery.trim().length < 2 ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">Type at least 2 characters.</div>
          ) : universal.isLoading && !universal.data ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">Searching…</div>
          ) : (
            <>
              {/* Core-100 R4 CORE-AY: a search refused by the server's search bound is said as such — never "nothing found". */}
              {universal.data?.refusal ? (
                <div className="px-3 py-3 text-sm text-destructive" role="alert" data-testid="universal-search-refused">
                  {universal.data.refusal}
                </div>
              ) : (
                <CommandEmpty>Nothing found in the records searched below.</CommandEmpty>
              )}
              {UNIVERSAL_GROUP_ORDER.map((group) => {
                const items = (universal.data?.results ?? []).filter((r) => r.group === group);
                if (items.length === 0) return null;
                return (
                  <CommandGroup key={group} heading={group}>
                    {items.map((r) => (
                      <CommandItem key={r.key} value={r.key} onSelect={() => handleUniversalSelect(r)} className="flex items-center gap-2 cursor-pointer">
                        {r.status && <div className={cn("w-2 h-2 rounded-full", getStatusBadge(r.status))} />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{r.title}</div>
                          {r.subtitle && <div className="text-xs text-muted-foreground truncate">{r.subtitle}{r.status ? ` • ${r.status}` : ''}</div>}
                          {r.target.note && <div className="text-xs text-muted-foreground" data-testid="universal-result-note">{r.target.note}</div>}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
              {universal.data && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-t" data-testid="universal-search-scope">
                  Searched: {universal.data.scope.join(' ')}
                </div>
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>

      {/* Signal Panel */}
      <SignalPanel
        open={showSignalPanel}
        onOpenChange={setShowSignalPanel}
        signals={signals}
        onAcknowledge={acknowledgeSignal}
        onDismiss={dismissSignal}
        isLoading={signalsLoading}
      />
    </>
  );
});
// ARC-A5: explicit displayName for React DevTools introspection.
// Without it, `memo(() => {...})` shows up as "Memo" in the
// component tree, masking the canonical name in profiling traces.
TopBar.displayName = 'TopBar';
